import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { DataService } from '../services/data.service';
import { DTRNextQuestionResponse } from '../models/dtr-types';
import { ResourceType } from '../models/ResourceType';
import { EndpointService } from '../services/endpoint.service';

@Injectable({
  providedIn: 'root',
})
export class AdaptiveQuestionnaireService {
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);

  static readonly adaptiveQuestionnaireExtensionUrl =
    'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-questionnaireAdaptive'; // NOSONAR: FHIR canonical URI, not a network endpoint

  static isAdaptiveQuestionnaire(questionnaire: fhirR4.Questionnaire) {
    return questionnaire.extension?.find((e) =>
      e.url.endsWith(
        AdaptiveQuestionnaireService.adaptiveQuestionnaireExtensionUrl,
      ),
    )?.valueBoolean;
  }

  async nextQuestion(qr: fhirR4.QuestionnaireResponse): Promise<{
    qr: fhirR4.QuestionnaireResponse;
    questionnaire: fhirR4.Questionnaire;
  }> {
    const params = this.createNextQuestionParameter(qr);
    const response = await this.dataService.post<DTRNextQuestionResponse>(
      params,
      this.endpointService.getNextQuestionUrl(),
    );
    if (this.isQuestionnaireResponse(response)) {
      return this.addMissingAdaptiveQrItems(
        response,
        this.hasMoreQuestions(qr, response),
      );
    } else {
      const nextQr = this.extractQuestionnaireResponse(response);
      return this.addMissingAdaptiveQrItems(
        nextQr,
        this.hasMoreQuestions(qr, nextQr),
      );
    }
  }

  /**
   * Depending on the IG followed, the QuestionnaireResponse could be in one of two parameters:
   * 'return' or 'questionnaire-response-out'.
   * Davinci IG: https://build.fhir.org/ig/HL7/davinci-dtr/en/OperationDefinition-DTR-Questionnaire-next-question.html
   * SDC IG: https://hl7.org/fhir/uv/sdc/OperationDefinition-Questionnaire-next-question.html
   * @param params
   */
  extractQuestionnaireResponse(
    params: fhirR4.Parameters,
  ): fhirR4.QuestionnaireResponse {
    const validParams = new Set(['return', 'questionnaire-response-out']);
    const qrParam = params.parameter?.find(
      (p) => p.name && validParams.has(p.name),
    );
    if (!qrParam?.resource) {
      throw new Error(
        'Parameters response missing required named parameter with QuestionnaireResponse resource',
      );
    }
    const resource = qrParam.resource;
    if (resource.resourceType !== 'QuestionnaireResponse') {
      throw new Error(
        `Expected QuestionnaireResponse but got ${resource.resourceType}`,
      );
    }
    return resource as fhirR4.QuestionnaireResponse;
  }

  hasMoreQuestions(
    prevQr: fhirR4.QuestionnaireResponse,
    nextQr: fhirR4.QuestionnaireResponse,
  ): boolean {
    const prevList = this.getLinkIds(this.getContainedQuestionnaire(prevQr));
    const nextList = this.getLinkIds(this.getContainedQuestionnaire(nextQr));
    if (prevList.length !== nextList.length) {
      return true;
    }
    const sortedPrev = [...prevList].sort((a, b) => a.localeCompare(b));
    const sortedNext = [...nextList].sort((a, b) => a.localeCompare(b));

    return !sortedPrev.every((linkId, index) => linkId === sortedNext[index]);
  }

  getLinkIds(q: fhirR4.QuestionnaireResponse | fhirR4.Questionnaire): string[] {
    const linkIds: string[] = [];

    if (!q.item) return linkIds;
    const stack: (
      | fhirR4.QuestionnaireResponseItem
      | fhirR4.QuestionnaireItem
    )[] = [...q.item];

    while (stack.length > 0) {
      const item = stack.shift()!;
      if (item.linkId) {
        linkIds.push(item.linkId);
      }
      if (item.item) {
        stack.push(...item.item);
      }
    }
    return linkIds;
  }

  getContainedQuestionnaire(
    qr: fhirR4.QuestionnaireResponse,
  ): fhirR4.Questionnaire {
    const q = qr['contained']?.find(
      (resource) => resource.resourceType === ResourceType.Questionnaire,
    );
    if (!q) {
      throw new Error('QuestionnaireResponse missing contained Questionnaire');
    }
    return q as fhirR4.Questionnaire;
  }

  addMissingAdaptiveQrItems(
    qr: fhirR4.QuestionnaireResponse,
    hasMoreQuestions: boolean,
  ): {
    qr: fhirR4.QuestionnaireResponse;
    questionnaire: fhirR4.Questionnaire;
  } {
    const containedQ = this.getContainedQuestionnaire(qr);
    const qrItems = qr.item ?? [];
    const newItems = (containedQ.item ?? []).filter(
      (item) =>
        item.linkId && !qrItems.some((qrItem) => qrItem.linkId === item.linkId),
    );
    return {
      questionnaire: containedQ,
      qr: {
        ...qr,
        item: qrItems.concat(newItems),
        status: hasMoreQuestions ? qr.status : 'completed',
      },
    };
  }

  createNextQuestionParameter(
    qr: fhirR4.QuestionnaireResponse,
  ): fhirR4.Parameters {
    const retVal = new fhirR4.Parameters();
    retVal.resourceType = ResourceType.Parameters;
    const qrParam = new fhirR4.ParametersParameter();
    qrParam.name = 'questionnaire-response';
    qrParam.resource = qr;
    retVal.parameter = [qrParam];
    return retVal;
  }

  isQuestionnaireResponse(
    response: DTRNextQuestionResponse,
  ): response is fhirR4.QuestionnaireResponse {
    return response?.resourceType === 'QuestionnaireResponse';
  }
}
